import Fuse from "fuse.js"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { SidebarFacetPopover } from "@/components/search/sidebar-facet-popover"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import type { TermFacetDescriptor, TermFacetOption } from "./facet-descriptor"

const INITIAL_VISIBLE_OPTIONS = 8
const SHOW_MORE_INCREMENT = 10

/**
 * A described term facet in the shared sidebar popover shell. Selected
 * values show as pills; the rest are searchable, progressively revealed
 * checkboxes.
 */
export function SidebarFacet({
  descriptor,
}: {
  descriptor: TermFacetDescriptor
}) {
  const { label, pillLabel, searchable, options, toggle } = descriptor
  const { t } = useTranslation("search")
  const [searchTerm, setSearchTerm] = useState("")
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_OPTIONS)
  const selected = useMemo(
    () => options.filter((option) => option.selected),
    [options],
  )
  const unselected = useMemo(
    () => options.filter((option) => !option.selected),
    [options],
  )
  const fuse = useMemo(
    () => new Fuse(unselected, { keys: ["label", "searchTerms"] }),
    [unselected],
  )

  if (options.length === 0) return null

  const displayed =
    searchable && searchTerm
      ? fuse.search(searchTerm).map(({ item }) => item)
      : unselected.slice(0, visibleCount)
  const hasMore = !searchTerm && unselected.length > visibleCount

  return (
    <SidebarFacetPopover
      label={label}
      selections={selected.map((option) => ({
        value: option.value,
        label: pillLabel(option.value),
      }))}
      onToggle={toggle}
      showSelectedPills
      hasAvailableOptions={displayed.length > 0}
      filter={
        searchable ? (
          <Input
            className="h-7 text-sm"
            placeholder={t("facet.filter_placeholder", { label })}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        ) : undefined
      }
    >
      <div className="mt-3 flex flex-col gap-1.5">
        {displayed.map((option) => (
          <TermFacetOptionControl
            key={option.value}
            option={option}
            onToggle={toggle}
          />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground mt-2 self-start text-xs"
          onClick={() =>
            setVisibleCount((count) => count + SHOW_MORE_INCREMENT)
          }
        >
          {t("common.show_more", { count: SHOW_MORE_INCREMENT })}
        </button>
      )}
    </SidebarFacetPopover>
  )
}

function TermFacetOptionControl({
  option,
  onToggle,
}: {
  option: TermFacetOption
  onToggle: (value: string) => void
}) {
  return (
    <label className="flex min-w-0 cursor-pointer items-start gap-2 text-sm">
      <Checkbox
        className="mt-0.5 shrink-0"
        aria-label={option.label}
        checked={false}
        onCheckedChange={() => onToggle(option.value)}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="break-words">{option.label}</span>
        {option.description && (
          <span className="text-muted-foreground text-xs break-words">
            {option.description}
          </span>
        )}
      </span>
      <span className="text-muted-foreground mt-0.5 shrink-0 tabular-nums">
        {option.count}
      </span>
    </label>
  )
}
